import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import * as moment from "moment-timezone";

import {
  IGetProductFilters,
  useProductsApi,
  IProduct
} from "src/core/products/api/products.api";
import { setProduct } from "src/core/products/store/products.actions";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { useDrop } from "src/core/drops/hooks";

const useProduct = (productId: number, filters?: IGetProductFilters | undefined) => {
  const [ isLoading, setLoading ] = React.useState(true);
  const dispatch = useDispatch();
  const [ getProductResponse, getProduct ] = useProductsApi("GET_PRODUCT");
  const [ saveProductResponse, saveProduct ] = useProductsApi("UPDATE_PRODUCT");
  const [ hasFetchedProduct, setHasFetchedProduct ] = React.useState(false);
  const [ { drop } ] = useDrop();

  const {
    product, currentOrder, installedModules
  } = useMappedState(({
    products, orders, global
  }: IStore) => ({
    product: products.product,
    currentOrder: orders.currentOrder,
    installedModules: global.settings ? global.settings.installedModules : []
  }));

  const reloadProduct = React.useCallback(() => {
    getProduct({
      productId,
      filters: {
        ...filters,
        branchId: drop?.id,
        effectiveDate: installedModules.includes("price-change") && currentOrder?.mode === "edit" ?
          currentOrder?.data?.orderDeliveryDate || moment.utc().toDate() : undefined
      }
    });
    setHasFetchedProduct(true);
  }, [
    getProduct,
    productId,
    filters,
    drop?.id,
    installedModules,
    currentOrder?.mode,
    currentOrder?.data?.orderDeliveryDate
  ]);

  const setCurrentProduct = React.useCallback((product: IProduct | null) => {
    dispatch(setProduct(product));
  }, [ dispatch ]);

  React.useEffect(() => {
    if (!hasFetchedProduct) {
      reloadProduct();
    }
  }, [ hasFetchedProduct, reloadProduct ]);

  React.useEffect(() => {
    if (saveProductResponse.data) {
      setCurrentProduct(saveProductResponse.data);
      toast.success("Product saved successfully");
    }

    if (saveProductResponse.error) {
      toast.error(`Could not save product - ${saveProductResponse.error.message}`);
    }
  }, [ saveProductResponse, setCurrentProduct ]);

  React.useEffect(() => {
    if (getProductResponse.data) {
      setCurrentProduct(getProductResponse.data);
      setLoading(false);
    }

    if (getProductResponse.error) {
      setLoading(false);
      dispatch(setGlobalError(getProductResponse.error));
    }
  }, [
    dispatch,
    getProductResponse,
    setCurrentProduct
  ]);

  return {
    isLoading,
    product,
    getProduct,
    reloadProduct,
    setCurrentProduct,
    saveProduct,
    isSavingProduct: saveProductResponse.pending || isLoading
  };
};

export default useProduct;
